import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  AccountBalance,
  Refresh,
  FilterList,
  CheckBoxOutlined,
  Add,
  TableChart,
  ViewList,
  Lock,
  Person,
} from '@mui/icons-material';
import { financeService, Account, AccountType, AccountStats } from '../../services/financeService';
import AccountTreeView from '../../components/finance/AccountTreeView';
import AccountRowActions from '../../components/finance/AccountRowActions';
import AccountStatsComponent from '../../components/finance/AccountStats';
import AccountDialog, { AccountFormData } from '../../components/finance/AccountDialog';
import GeneralLedgerWorkspace from './GeneralLedgerWorkspace';
import ChartOfAccountsWorkspace from './ChartOfAccountsWorkspace';
import CustomerCollection360Page from './CustomerCollection360Page';
import FinancialReportingControlCenter from './FinancialReportingControlCenter';
import GSTReportsPage from './GSTReportsPage';
import TDSReportsPage from './TDSReportsPage';
import AgeingReportsPage from './AgeingReportsPage';
import DayBookPage from './DayBookPage';
import BankReconciliationPage from './BankReconciliationPage';
import FinanceCommandCenter from './FinanceCommandCenter';
import CollectionsPage from './CollectionsPage';
import CostProfitControlCenter from './CostProfitControlCenter';
import CostCenter360Page from './CostCenter360Page';
import ProfitCenter360Page from './ProfitCenter360Page';
import BudgetControlCenter from './BudgetControlCenter';
import Budget360Page from './Budget360Page';
import RecurringJournalControlCenter from './RecurringJournalControlCenter';
import PeriodCloseControlCenter from './PeriodCloseControlCenter';
import Period360Page from './Period360Page';
import CreditDebitNotesPage from './CreditDebitNotesPage';
import JournalEntryNewPage from './JournalEntryNewPage';
import JournalControlCenter from './JournalControlCenter';
import Journal360Page from './Journal360Page';
import ExpensePage from './ExpensePage';
import ExpenseFormPage from './ExpenseFormPage';
import ExpenseDetailPage from './ExpenseDetailPage';
import PaymentAllocationPage from './PaymentAllocationPage';
import BankStatementImportPage from './BankStatementImportPage';

/**
 * Type tint for the code glyph and the type pill.
 *
 * Identity colouring, kept deliberately distinct from the status palette so a
 * green ASSET pill is never read as a "good" signal — status in this table is
 * carried by the Active/Inactive dot alone.
 */
const accountTypeTints: Record<string, string> = {
  ASSET: '#059669',
  LIABILITY: '#dc2626',
  EQUITY: '#7c3aed',
  INCOME: '#0284c7',
  EXPENSE: '#ea580c',
};

type ViewMode = 'tree' | 'table';

const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [hierarchyAccounts, setHierarchyAccounts] = useState<Account[]>([]);
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<AccountType | ''>('');
  const [filterActive, setFilterActive] = useState<boolean | ''>('');
  const [viewMode, setViewMode] = useState<ViewMode>('tree');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { accountType?: AccountType; isActive?: boolean } = {};
      if (filterType) filters.accountType = filterType;
      if (filterActive !== '') filters.isActive = filterActive;

      const result = await financeService.getAccounts(filters);
      setAccounts(result.accounts);
      setStats(result.stats);

      // Also fetch hierarchy for tree view
      const hierarchy = await financeService.getAccountHierarchy();
      setHierarchyAccounts(hierarchy);
    } catch (err: any) {
      console.error('Error fetching accounts:', err);
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [filterType, filterActive]);

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setDialogOpen(true);
  };

  const handleEditAccount = (account: Account) => {
    setEditingAccount(account);
    setDialogOpen(true);
  };

  const handleDeleteClick = (account: Account) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;

    setDeleteLoading(true);
    try {
      await financeService.deleteAccount(accountToDelete.id);
      setSuccessMessage(`Account "${accountToDelete.account_name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
      fetchAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDialogSave = async (formData: AccountFormData) => {
    if (editingAccount) {
      // Update existing account
      await financeService.updateAccount(editingAccount.id, {
        account_name: formData.accountName,
        is_active: formData.isActive,
      });
      setSuccessMessage('Account updated successfully');
    } else {
      // Create new account
      await financeService.createAccount({
        account_code: formData.accountCode,
        account_name: formData.accountName,
        account_type: formData.accountType,
        parent_id: formData.parentId,
        is_active: formData.isActive,
      } as any);
      setSuccessMessage('Account created successfully');
    }
    fetchAccounts();
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.25 }}>
            Chart of Accounts
            <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>
              {' '}— Indian Standard
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete account structure with GST and TDS compliance accounts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{ mr: 1 }}
          >
            <ToggleButton value="tree">
              <Tooltip title="Tree View">
                <ViewList />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title="Table View">
                <TableChart />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAccounts} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} size="small" onClick={handleAddAccount}>
            Add Account
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && <AccountStatsComponent stats={stats} />}

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Account Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as AccountType | '')}
            sx={{ minWidth: 220, flexGrow: { xs: 1, md: 0 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="ASSET">Asset</MenuItem>
            <MenuItem value="LIABILITY">Liability</MenuItem>
            <MenuItem value="EQUITY">Equity</MenuItem>
            <MenuItem value="INCOME">Income</MenuItem>
            <MenuItem value="EXPENSE">Expense</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Status"
            value={filterActive === '' ? '' : filterActive ? 'active' : 'inactive'}
            onChange={(e) => {
              if (e.target.value === '') setFilterActive('');
              else setFilterActive(e.target.value === 'active');
            }}
            sx={{ minWidth: 200, flexGrow: { xs: 1, md: 0 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CheckBoxOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="active">Active Only</MenuItem>
            <MenuItem value="inactive">Inactive Only</MenuItem>
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="body2" color="text.secondary">
            Showing <strong>{accounts.length}</strong> accounts
            {' ('}
            {/* The view name is the control that changes it — the toggle up in
                the header is easy to miss from down here. */}
            <Box
              component="span"
              role="button"
              tabIndex={0}
              onClick={() => setViewMode(viewMode === 'tree' ? 'table' : 'tree')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setViewMode(viewMode === 'tree' ? 'table' : 'tree');
                }
              }}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {viewMode === 'tree' ? 'Tree View' : 'Table View'}
            </Box>
            {')'}
          </Typography>
        </Box>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'tree' ? (
        <AccountTreeView
          accounts={hierarchyAccounts}
          onEdit={handleEditAccount}
          onDelete={handleDeleteClick}
        />
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Account Code', 'Account Name', 'Account Type', 'Default', 'Status', ''].map((h, i) => (
                    <TableCell
                      key={h || i}
                      align={i === 5 ? 'right' : 'left'}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1.75,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        No accounts match these filters
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Clear the type or status filter to see the full chart.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => {
                    const tint = accountTypeTints[account.account_type] || '#64748b';
                    return (
                      <TableRow
                        key={account.id}
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:last-child td': { border: 0 },
                        }}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            {/* Type-tinted glyph so the ledger's shape is
                                scannable without reading the type column. */}
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: 'grid',
                                placeItems: 'center',
                                backgroundColor: `${tint}1a`,
                                flexShrink: 0,
                              }}
                            >
                              <AccountBalance sx={{ fontSize: 17, color: tint }} />
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                color: tint,
                              }}
                            >
                              {account.account_code}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {account.account_name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              px: 1,
                              py: 0.375,
                              borderRadius: 1,
                              fontSize: '0.6875rem',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              color: tint,
                              backgroundColor: `${tint}14`,
                            }}
                          >
                            {account.account_type}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Chip
                            icon={account.is_default ? <Lock sx={{ fontSize: 13 }} /> : <Person sx={{ fontSize: 13 }} />}
                            label={account.is_default ? 'Default' : 'Custom'}
                            size="small"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.75rem' }}
                          />
                        </TableCell>

                        <TableCell>
                          {/* Dot plus word — status is never colour alone. */}
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: account.is_active ? 'success.main' : 'text.disabled',
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: account.is_active ? 'success.main' : 'text.disabled',
                              }}
                            >
                              {account.is_active ? 'Active' : 'Inactive'}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          <AccountRowActions
                            account={account}
                            onEdit={handleEditAccount}
                            onDelete={handleDeleteClick}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Account Dialog */}
      <AccountDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleDialogSave}
        account={editingAccount}
        parentAccounts={accounts}
        isLoading={loading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the account "{accountToDelete?.account_name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const JournalEntries: React.FC = () => <JournalControlCenter />;

const FinancePage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<FinanceCommandCenter />} />
      <Route path="command-center" element={<FinanceCommandCenter />} />
      <Route path="overview" element={<Navigate to="/finance" replace />} />
      <Route path="accounts" element={<ChartOfAccountsWorkspace />} />
      <Route path="journal" element={<JournalEntries />} />
      <Route path="journal/new" element={<JournalEntryNewPage />} />
      <Route path="journal/:id" element={<Journal360Page />} />
      <Route path="ledger" element={<GeneralLedgerWorkspace />} />
      <Route path="reports" element={<FinancialReportingControlCenter />} />
      <Route path="gst-reports" element={<GSTReportsPage />} />
      <Route path="tds-reports" element={<TDSReportsPage />} />
      <Route path="ageing" element={<AgeingReportsPage />} />
      <Route path="day-book" element={<DayBookPage />} />
      <Route path="bank-reconciliation" element={<BankReconciliationPage />} />
      {/* New enterprise finance workspaces */}
      <Route path="credit-debit-notes" element={<CreditDebitNotesPage />} />
      <Route path="collections" element={<CollectionsPage />} />
      <Route path="collections/:customerId" element={<CustomerCollection360Page />} />
      <Route path="budgets" element={<BudgetControlCenter />} />
      <Route path="budgets/:id" element={<Budget360Page />} />
      <Route path="cost-centers" element={<CostProfitControlCenter />} />
      <Route path="cost-centers/:id" element={<CostCenter360Page />} />
      <Route path="profit-centers/:id" element={<ProfitCenter360Page />} />
      <Route path="recurring-journals" element={<RecurringJournalControlCenter />} />
      <Route path="period-close" element={<PeriodCloseControlCenter />} />
      <Route path="period-close/:fy/:period" element={<Period360Page />} />
      <Route path="expenses" element={<ExpensePage />} />
      <Route path="expenses/new" element={<ExpenseFormPage />} />
      <Route path="expenses/:id/edit" element={<ExpenseFormPage />} />
      <Route path="expenses/:id" element={<ExpenseDetailPage />} />
      <Route path="payment-allocations" element={<PaymentAllocationPage />} />
      <Route path="bank-statements" element={<BankStatementImportPage />} />
    </Routes>
  );
};

export default FinancePage;